import { useState, ChangeEvent } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Separator } from '@creanizer/ui'
import { FileText, X } from 'lucide-react'
import type { SkillInput, SavedSkill, SavedCharacter, SavedItem, SavedWorldBuilding } from '@creanizer/types'

interface UploadedFile {
  id: string
  name: string
  content: string
}
import { generateCharacterContext, generateItemContext, generateEnemyContext } from '@creanizer/utils'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { SkillFormStandard } from './SkillFormStandard'
import { SkillFormIdea } from './SkillFormIdea'
import { SkillFormBatch } from './SkillFormBatch'
import { SkillFormEvolution } from './SkillFormEvolution'
import { SkillFormCombination } from './SkillFormCombination'

interface SkillFormProps {
  input: SkillInput
  onChange: (input: SkillInput) => void
  onModeChange: (mode: string) => void
  worlds: SavedWorldBuilding[]
  characters: SavedCharacter[]
  items: SavedItem[]
  savedSkills: SavedSkill[]
  selectedWorldId: string
  setSelectedWorldId: (id: string) => void
  selectedCharacterId: string
  setSelectedCharacterId: (id: string) => void
  selectedItemId: string
  setSelectedItemId: (id: string) => void
  selectedEnemyId: string
  setSelectedEnemyId: (id: string) => void
  selectedBaseSkillId: string
  setSelectedBaseSkillId: (id: string) => void
  selectedCombinationSkills: string[]
  setSelectedCombinationSkills: (ids: string[]) => void
  useStreaming: boolean
  setUseStreaming: (value: boolean) => void
}

export function SkillForm({
  input,
  onChange,
  onModeChange,
  worlds,
  characters,
  items,
  savedSkills,
  selectedWorldId,
  setSelectedWorldId,
  selectedCharacterId,
  setSelectedCharacterId,
  selectedItemId,
  setSelectedItemId,
  selectedEnemyId,
  setSelectedEnemyId,
  selectedBaseSkillId,
  setSelectedBaseSkillId,
  selectedCombinationSkills,
  setSelectedCombinationSkills,
  useStreaming,
  setUseStreaming
}: SkillFormProps) {
  const { enemies } = useEnemyStore()
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])

  // テキストファイルアップロード処理（複数対応）
  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const newFiles: UploadedFile[] = []

    for (const file of Array.from(files)) {
      try {
        const text = await file.text()
        newFiles.push({
          id: `file_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: file.name,
          content: text
        })
      } catch (error) {
        console.error('ファイル読み込みエラー:', error)
        alert(`${file.name} の読み込みに失敗しました`)
      }
    }

    if (newFiles.length > 0) {
      const allFiles = [...uploadedFiles, ...newFiles]
      setUploadedFiles(allFiles)
      const allFilesContent = allFiles
        .map(file => `【ファイル: ${file.name}】\n${file.content}`)
        .join('\n\n')
      onChange({ ...input, uploadedContent: allFilesContent || undefined })
    }

    e.target.value = ''
  }

  const handleFileRemove = (fileId: string) => {
    const newFiles = uploadedFiles.filter(f => f.id !== fileId)
    setUploadedFiles(newFiles)
    const allFilesContent = newFiles
      .map(file => `【ファイル: ${file.name}】\n${file.content}`)
      .join('\n\n')
    onChange({ ...input, uploadedContent: allFilesContent || undefined })
  }
  
  // 世界観コンテキスト生成用（これは特殊なのでローカルで定義）
  const generateWorldContext = (world: SavedWorldBuilding) => {
    return JSON.stringify({
      name: world.result.name,
      concept: world.result.concept,
      magicSystem: world.result.details.magicSystem,
      technology: world.result.details.technology,
      cultures: world.result.details.cultures
    })
  }
  
  return (
    <div className="space-y-6">
      {/* 共通設定セクション */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">共通設定</h3>
        <div className="space-y-4">
          {/* 世界観選択 */}
          {worlds.length > 0 && (
            <div className="space-y-2">
              <Label>世界観を選択（オプション）</Label>
              <Select value={selectedWorldId || 'none'} onValueChange={(value) => {
                if (value === 'none') {
                  setSelectedWorldId('')
                  onChange({ ...input, worldContext: undefined })
                } else {
                  setSelectedWorldId(value)
                  const world = worlds.find(w => w.id === value)
                  if (world) {
                    onChange({ ...input, worldContext: generateWorldContext(world) })
                  }
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="世界観を選択..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">なし</SelectItem>
                  {worlds.map((world) => (
                    <SelectItem key={world.id} value={world.id}>
                      {world.result.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedWorldId && (
                <p className="text-sm text-muted-foreground">
                  選択した世界観のスキル体系に適合したスキルが生成されます
                </p>
              )}
            </div>
          )}
          
          {/* キャラクター連携 */}
          {characters.length > 0 && (
            <div className="space-y-2">
              <Label>使用キャラクター（オプション）</Label>
              <Select
                value={selectedCharacterId || 'none'}
                onValueChange={(value) => {
                  if (value === 'none') {
                    setSelectedCharacterId('')
                    onChange({ 
                      ...input, 
                      relatedCharacterContext: undefined,
                      characterClass: undefined 
                    })
                  } else {
                    const character = characters.find(c => c.id === value)
                    if (character) {
                      const context = generateCharacterContext(character)
                      onChange({ 
                        ...input, 
                        relatedCharacterContext: context,
                        characterClass: character.result.background?.occupation || character.result.role
                      })
                      setSelectedCharacterId(value)
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="キャラクターを選択..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">なし</SelectItem>
                  {characters.map((char) => (
                    <SelectItem key={char.id} value={char.id}>
                      {char.result.name} ({char.result.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCharacterId && (
                <Alert>
                  <AlertDescription>
                    選択したキャラクターの能力・性格・戦闘スタイルに適合したスキルが生成されます
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          
          {/* エネミー連携 */}
          {enemies.length > 0 && (
            <div className="space-y-2">
              <Label>関連エネミー（オプション）</Label>
              <Select
                value={selectedEnemyId || 'none'}
                onValueChange={(value) => {
                  if (value === 'none') {
                    setSelectedEnemyId('')
                    onChange({ 
                      ...input, 
                      relatedEnemyContext: undefined,
                      enemyId: undefined
                    })
                  } else {
                    const enemy = enemies.find(e => e.id === value)
                    if (enemy) {
                      const context = generateEnemyContext(enemy)
                      onChange({ 
                        ...input, 
                        relatedEnemyContext: context,
                        enemyId: value
                      })
                      setSelectedEnemyId(value)
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="エネミーを選択..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">なし</SelectItem>
                  {enemies.map((enemy) => (
                    <SelectItem key={enemy.id} value={enemy.id}>
                      {enemy.result.basicInfo.name} ({enemy.result.basicInfo.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedEnemyId && (
                <Alert>
                  <AlertDescription>
                    選択したエネミーの能力・行動パターン・弱点に対応したスキルが生成されます
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          
          {/* アイテム連携 */}
          {items.length > 0 && (
            <div className="space-y-2">
              <Label>関連アイテム（オプション）</Label>
              <Select
                value={selectedItemId || 'none'}
                onValueChange={(value) => {
                  if (value === 'none') {
                    setSelectedItemId('')
                    onChange({ ...input, relatedItemContext: undefined })
                  } else {
                    const item = items.find(i => i.id === value)
                    if (item) {
                      const context = generateItemContext(item)
                      onChange({ ...input, relatedItemContext: context })
                      setSelectedItemId(value)
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="アイテムを選択..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">なし</SelectItem>
                  {items.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.result.basicInfo.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <Separator />
          
          {/* 生成オプション */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="useProMode"
                checked={input.useProMode || false}
                onCheckedChange={(checked) =>
                  onChange({ ...input, useProMode: checked as boolean })
                }
              />
              <Label htmlFor="useProMode">
                Proモードを使用（より詳細で高品質な生成）
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="useGemini3FlashMode"
                checked={input.useGemini3FlashMode || false}
                onCheckedChange={(checked) =>
                  onChange({ ...input, useGemini3FlashMode: checked as boolean })
                }
              />
              <Label htmlFor="useGemini3FlashMode">
                Gemini 3 Flash モード（試験版）
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="useGemini3Mode"
                checked={input.useGemini3Mode || false}
                onCheckedChange={(checked) =>
                  onChange({ ...input, useGemini3Mode: checked as boolean })
                }
              />
              <Label htmlFor="useGemini3Mode">
                Gemini 3 Pro モード（有料APIのみ・試験版）
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="useStreaming"
                checked={useStreaming}
                onCheckedChange={(checked) =>
                  setUseStreaming(checked as boolean)
                }
              />
              <Label htmlFor="useStreaming">
                ストリーミングモードを使用（リアルタイム生成）
              </Label>
            </div>
          </div>

          <Separator />

          {/* 参考資料ファイルアップロード */}
          <div className="space-y-2">
            <Label htmlFor="fileUpload">参考資料ファイル（オプション）</Label>
            <Input
              id="fileUpload"
              type="file"
              accept=".txt,.md,.json"
              multiple
              onChange={handleFileUpload}
            />
            <p className="text-xs text-muted-foreground">
              テキストファイル（.txt/.md/.json）を複数アップロードして参考資料として使用できます
            </p>

            {/* アップロード済みファイル一覧 */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-2 mt-2">
                {uploadedFiles.map(file => (
                  <div key={file.id} className="flex items-center gap-2 p-2 bg-muted rounded">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm flex-1 truncate">{file.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleFileRemove(file.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* モード別設定セクション */}
      <div className="card">
        <div>
          <Tabs 
            defaultValue="standard" 
            value={input.mode || 'standard'} 
            onValueChange={onModeChange}
          >
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="standard">標準</TabsTrigger>
              <TabsTrigger value="idea">アイディア</TabsTrigger>
              <TabsTrigger value="batch">バッチ</TabsTrigger>
              <TabsTrigger value="evolution">進化</TabsTrigger>
              <TabsTrigger value="combination">組み合わせ</TabsTrigger>
            </TabsList>
            
            <TabsContent value="standard">
              <SkillFormStandard
                input={input}
                onChange={onChange}
              />
            </TabsContent>
            
            <TabsContent value="idea">
              <SkillFormIdea
                input={input}
                onChange={onChange}
              />
            </TabsContent>
            
            <TabsContent value="batch">
              <SkillFormBatch
                input={input}
                onChange={onChange}
              />
            </TabsContent>
            
            <TabsContent value="evolution">
              <SkillFormEvolution
                input={input}
                onChange={onChange}
                savedSkills={savedSkills}
                selectedBaseSkillId={selectedBaseSkillId}
                setSelectedBaseSkillId={setSelectedBaseSkillId}
              />
            </TabsContent>
            
            <TabsContent value="combination">
              <SkillFormCombination
                input={input}
                onChange={onChange}
                savedSkills={savedSkills}
                selectedCombinationSkills={selectedCombinationSkills}
                setSelectedCombinationSkills={setSelectedCombinationSkills}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}